import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getCashBankList } from '../../../../redux/actions/SocietyPortal/CashBankAction/CashBankAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_ACCOUNTS_CASH_AND_BANK_BOOK,
} from '../../../../Routing/routes';

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const maskAccountNumber = (num) => {
    const str = String(num || '');
    if (!str) return '****';
    return `**** ${str.slice(-4)}`;
};

const CashAndBank = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { listData, listLoading } = useSelector(state => state.CashBankReducer || {});

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [search, setSearch] = useState('');

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getCashBankList({ societyId, year: financialYear }));
        }
    }, [dispatch, societyId, financialYear, refreshKey]);

    const accounts = Array.isArray(listData) ? listData : [];

    const filteredAccounts = useMemo(() => {
        if (!search.trim()) return accounts;
        const q = search.toLowerCase();
        return accounts.filter(a =>
            a.name?.toLowerCase().includes(q) ||
            a.gl_code?.toLowerCase().includes(q) ||
            a.account_no?.toString().includes(q) ||
            a.ifsc_code?.toLowerCase().includes(q)
        );
    }, [accounts, search]);

    const handleCardClick = (glCode) => {
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_ACCOUNTS_CASH_AND_BANK_BOOK.replace(':accountId', encodeURIComponent(glCode))}?year=${financialYear}`);
    };

    const handleRefresh = () => {
        dispatch(getCashBankList({ societyId, year: financialYear }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-mb-5">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center tw-shadow-md">
                            <Icon icon="hugeicons:bank" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Cash &amp; Bank</h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                Select a cash or bank account to view transactions
                            </p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <SearchableDropdown
                            value={financialYear}
                            onChange={setFinancialYear}
                            options={financialYears}
                            placeholder="Financial Year"
                            clearable={false}
                            small
                        />
                        <button
                            onClick={handleRefresh}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-bg-white tw-border tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
                            title="Refresh"
                        >
                            <Icon icon="mdi:refresh" className="tw-text-sm" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="tw-mb-4">
                <div className="tw-relative tw-max-w-xs">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, GL code, account no..."
                        className="tw-w-full tw-pl-8 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
            </div>

            {/* Account Cards */}
            {listLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                        <span className="tw-text-xs tw-text-gray-400">Loading accounts...</span>
                    </div>
                </div>
            ) : filteredAccounts.length > 0 ? (
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    {filteredAccounts.map(account => {
                        const isCash = account.account_type === 'cash';
                        const balance = parseFloat(account.current_balance) || 0;
                        const isCredit = account.balance_type === 'Cr';
                        return (
                            <div
                                key={account.id}
                                onClick={() => handleCardClick(account.gl_code)}
                                className="tw-group tw-relative tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm hover:tw-shadow-md hover:tw-border-rose-200 tw-transition-all tw-cursor-pointer tw-overflow-hidden"
                            >
                                {/* Top accent bar */}
                                <div className={`tw-h-1 tw-bg-gradient-to-r ${isCash ? 'tw-from-amber-400 tw-to-orange-500' : 'tw-from-rose-400 tw-to-pink-500'}`}></div>

                                <div className="tw-p-4">
                                    {/* Header with icon */}
                                    <div className="tw-flex tw-items-start tw-gap-3 tw-mb-4">
                                        <div className={`tw-w-11 tw-h-11 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-colors ${isCash ? 'tw-bg-amber-50 group-hover:tw-bg-amber-100' : 'tw-bg-rose-50 group-hover:tw-bg-rose-100'}`}>
                                            <Icon icon={isCash ? 'hugeicons:money-bag-02' : 'hugeicons:bank'} className={`tw-text-lg ${isCash ? 'tw-text-amber-500' : 'tw-text-rose-500'}`} />
                                        </div>
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">
                                                {account.name || '—'}
                                            </h3>
                                            <p className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5 tw-uppercase tw-tracking-wide tw-font-medium">
                                                {isCash ? 'Cash Account' : 'Bank Account'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="tw-space-y-2 tw-mb-4">
                                        <div className="tw-flex tw-items-center tw-justify-between">
                                            <span className="tw-text-[11px] tw-text-gray-500">GL Code:</span>
                                            <span className="tw-text-[11px] tw-font-medium tw-text-gray-700">{account.gl_code || '—'}</span>
                                        </div>
                                        {!isCash && (
                                            <>
                                                <div className="tw-flex tw-items-center tw-justify-between">
                                                    <span className="tw-text-[11px] tw-text-gray-500">Account No:</span>
                                                    <span className="tw-text-[11px] tw-font-medium tw-text-gray-700">{maskAccountNumber(account.account_no)}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-justify-between">
                                                    <span className="tw-text-[11px] tw-text-gray-500">IFSC:</span>
                                                    <span className="tw-text-[11px] tw-font-medium tw-text-gray-700">{account.ifsc_code || '—'}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Balance */}
                                    <div className="tw-pt-3 tw-border-t tw-border-gray-100">
                                        <div className="tw-flex tw-items-center tw-justify-between">
                                            <span className="tw-text-[11px] tw-text-gray-500 tw-font-medium">Current Balance:</span>
                                            <div className="tw-flex tw-items-baseline tw-gap-1">
                                                <span className={`tw-text-sm tw-font-bold ${isCredit ? 'tw-text-red-600' : 'tw-text-emerald-600'}`}>
                                                    ₹ {formatCurrency(balance)}
                                                </span>
                                                {account.balance_type && (
                                                    <span className={`tw-text-[10px] tw-font-semibold ${isCredit ? 'tw-text-red-500' : 'tw-text-emerald-500'}`}>{account.balance_type}</span>
                                                )}
                                            </div>
                                        </div>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-text-right tw-mt-1 group-hover:tw-text-rose-500 tw-transition-colors">
                                            Click to view transactions →
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-py-16">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <Icon icon="hugeicons:bank" className="tw-text-3xl tw-text-gray-300" />
                        <p className="tw-text-xs tw-text-gray-400">No cash or bank accounts found</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashAndBank;
